import { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';
import { logAdminAction } from '../../utils/auditLogger';

const AVAILABLE_COINS = [
  { value: 'RCN', label: 'RCN (Test)' },
  { value: 'FLCV', label: 'FLCV' },
  { value: 'PETCN', label: 'PETCN' },
  { value: 'FLCHU', label: 'FLCHU' },
  { value: 'TRLCN', label: 'TRLCN' },
  { value: 'SHPCN', label: 'SHPCN' },
  { value: 'VPVB', label: 'VPVB' },
  { value: 'GLDCN', label: 'GLDCN' },
  { value: 'CHRTC', label: 'CHRTC' },
  { value: 'HLTCN', label: 'HLTCN' }
];

export default function CreateWalletUser({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    user_id: Date.now().toString(),
    details: {
      first_name: '',
      last_name: '',
      country: '',
      city: '',
      balances: {}
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = {
        ...formData,
        created_at: new Date(),
        last_updated: new Date(),
        email_verified: false,
        document_verified: false,
        details: {
          ...formData.details,
          status: '1',
          account_type: '1'
        }
      };

      const docRef = await addDoc(collection(db, 'walletUsers'), userData);

      await logAdminAction('user.create', {
        userId: userData.user_id,
        email: userData.email
      });

      toast.success('Felhasználó sikeresen létrehozva');
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Hiba történt a felhasználó létrehozása során');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full">
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-xl font-semibold">Új Wallet Felhasználó</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  email: e.target.value
                }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User ID
              </label>
              <input
                type="text"
                required
                value={formData.user_id}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  user_id: e.target.value
                }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vezetéknév
              </label>
              <input
                type="text"
                value={formData.details.last_name}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  details: {
                    ...prev.details,
                    last_name: e.target.value
                  }
                }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Keresztnév
              </label>
              <input
                type="text"
                value={formData.details.first_name}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  details: {
                    ...prev.details,
                    first_name: e.target.value
                  }
                }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ország
              </label>
              <input
                type="text"
                value={formData.details.country}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  details: {
                    ...prev.details,
                    country: e.target.value
                  }
                }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Város
              </label>
              <input
                type="text"
                value={formData.details.city}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  details: {
                    ...prev.details,
                    city: e.target.value
                  }
                }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kezdő Egyenlegek
            </label>
            <div className="grid grid-cols-2 gap-4">
              {AVAILABLE_COINS.map(coin => (
                <div key={coin.value} className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">{coin.label}:</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.details.balances[coin.value] || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      details: {
                        ...prev.details,
                        balances: {
                          ...prev.details.balances,
                          [coin.value]: e.target.value
                        }
                      }
                    }))}
                    className="flex-1 px-2 py-1 border border-gray-300 rounded-md"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Mégse
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? (
                <Icons.Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                'Létrehozás'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 